import Konva from "konva";
import Point from "./Point";
import { ICONS, loadImageAsset } from "./whiteboard_util";

const STROKE_MAP = [2, 4, 8];
const STROKE_MAP_RADIUS = [0, 2, 4, 6];
const RADIUS_MAP = [15, 25, 40];
const PATH_MAP = [0.7, 1.3, 2];
const FONT_SIZE_MAP = [16, 25, 30];
const FONT_SIZE_MAP_OUTLINE = [14, 17, 25];
const IMAGE_SIZE_MAP = [15, 30, 50];
const PATTERN_MAP = [25, 40, 100];
const PLAYER_HITBOX_PADDING = 12;

function addPlayerHitbox(group, radius) {
    const hitbox = new Konva.Circle({
        radius: radius + PLAYER_HITBOX_PADDING,
        fill: "rgba(0, 0, 0, 0.01)",
        stroke: "rgba(14, 140, 233, 0)",
        strokeWidth: 3,
        dash: [8, 4],
        name: "player-hitbox",
    });
    group.add(hitbox);
    return hitbox;
}

function showPlayerHitbox(hitbox) {
    if (!hitbox) return;
    hitbox.stroke("#0e8ce9");
}

export function CNTextboxFactory(data) {
    const toolName = data.d[6];
    switch (toolName) {
        case "rect":
            return new Rect(data);
        case "rectOutline":
            return new RectOutline(data);
        case "circle":
            return new Circle(data);
        case "circleOutline":
            return new CircleOutline(data);
        case "circleFilled":
            return new CircleFilled(data);
        case "circleFixed":
            return new CircleFixed(data);
        case "Cross":
            return new Cross(data);
        case "circleWithCross":
            return new CircleWithCross(data);
        case "ShadedCross":
            return new ShadedCross(data);
    }

    if (toolName.startsWith("offense:basket")) {
        return new OffenseBasket(data);
    } else if (toolName.startsWith("defense:x")) {
        return new PlayerX(data);
    } else if (toolName.startsWith("offense:")) {
        return new Offense(data);
    } else if (toolName.startsWith("defense:")) {
        return new Defense(data);
    } else if (toolName.startsWith("icon:")) {
        return new Icon(data);
    }
}

export class CNTextbox {
    constructor(tool) {
        this.tool = tool;
        this.data = tool;
        this.color = tool.d[0];
        this.bgColor = tool.d[1];
        this.size = tool.d[2];
        this.x = tool.d[3];
        this.y = tool.d[4];
        this.id = tool.d[5];
        this.name = tool.d[6];
        this.version = tool.v;
        this.removerImage = tool.removerImage;
        this.basketImage = tool.basketImage;

        this.remover = this.buildRemover();
    }

    pasteData(inc) {
        const data = this.data.d;
        return [
            data[0],
            data[1],
            data[2],
            this.element.x() + 30 * inc,
            this.element.y() + 30 * inc,
            "tx" + +new Date(),
            data[6],
            data[7],
        ];
    }

    setClickAction(callback) {
        const handleClick = (event) => {
            showPlayerHitbox(this.hitbox);
            callback(event);
        };
        this.element.on("click", handleClick);
        this.element.on("tap", handleClick);
    }

    setDragStart(callback) {
        this.element.on("dragstart", (e) => callback(e));
    }

    setDragEnd(callback) {
        this.element.on("dragend", (e) => callback(e));
    }

    buildRemover() {
        const remover = new Konva.Image({
            image: this.removerImage,
            width: 20,
            height: 20,
            name: "remover",
            x: -40,
            y: -40,
            visible: false,
        });
        remover.on("mouseover", () => {
            document.body.style.cursor = "pointer";
        });
        remover.on("mouseout", () => {
            document.body.style.cursor = "default";
        });

        return remover;
    }

    setRemoverAction(callback) {
        this.remover.on("click", () => {
            callback();
        });
        this.remover.on("tap", () => {
            callback();
        });
    }

    get() {
        return null;
    }

    text() {
        return this.name
            .replace("offense:", "")
            .replace("defense:", "")
            .replace("basket-", "")
            .replace("x-", "")
            .replace(" ", "")
            .replace("\n", "")
            .toUpperCase();
    }

    static playerText(t) {
        return t
            .replace("offense:", "")
            .replace("defense:", "")
            .replace("basket-", "")
            .replace("x-", "")
            .replace(" ", "")
            .replace("\n", "")
            .toUpperCase();
    }
}

export class Circle extends CNTextbox {
    constructor(data) {
        super(data);

        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    resizeMove(x, y, rx, ry) {
        this.element.x(x);
        this.element.y(y);
        this.circle.radiusX(rx);
        this.circle.radiusY(ry);
    }

    setColor(color) {
        this.color = color;
        this.circle.fill(color);
        this.circle.stroke(color);
    }

    static toJSON(attrs, scale) {
        const x = attrs.x;
        const y = attrs.y;
        const r = attrs.radius * scale.x;
        const id = attrs.id;
        const startX = x;
        const startY = y;
        const endX = x + r;
        const endY = y + r;

        return {
            t: "addTextBox",
            d: [
                attrs.fill,
                attrs.fill,
                attrs.strokeWidth / 3,
                startX,
                startY,
                id,
                attrs.name,
                false,
                endX,
                endY,
            ],
        };
    }

    getVars() {
        const startX = this.tool.d[3];
        const startY = this.tool.d[4];
        const endX = this.tool.d[8];
        const endY = this.tool.d[9];

        const start = new Point(startX, startY);
        const cur = new Point(endX, endY);
        const r = cur.distTo(start);

        return {
            x: startX,
            y: startY,
            r,
        };
    }

    buildElement() {
        const { x, y, r } = this.getVars();

        this.circle = new Konva.Ellipse({
            radiusX: r,
            radiusY: r,
            stroke: this.color,
            strokeWidth: this.size * 3,
            fill: this.color,
            opacity: 0.2,
        });

        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            name: this.name,
            x,
            y,
        });

        group.setAttr("radius", r);

        this.remover.x(-r);
        this.remover.y(-r);

        group.add(this.circle);
        group.add(this.remover);
        return group;
    }
}

export class CircleOutline extends Circle {
    constructor(data) {
        super(data);

        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    resizeMove(x, y, rx, ry) {
        this.element.x(x);
        this.element.y(y);
        this.circle.radiusX(rx);
        this.circle.radiusY(ry);
    }

    setColor(color) {
        this.circle.stroke(color);
    }

    static toJSON(attrs, scale) {
        const x = attrs.x;
        const y = attrs.y;
        const r = attrs.radius * scale.x;
        const id = attrs.id;
        const startX = x;
        const startY = y;
        const endX = x + r;
        const endY = y + r;

        return {
            t: "addTextBox",
            d: [
                attrs.stroke,
                attrs.stroke,
                attrs.strokeWidth / 3,
                startX,
                startY,
                id,
                "circleOutline",
                false,
                endX,
                endY,
            ],
        };
    }
    buildElement() {
        const { x, y, r } = this.getVars();

        this.circle = new Konva.Ellipse({
            radiusX: r,
            radiusY: r,
            stroke: this.color,
            strokeWidth: STROKE_MAP[this.size],
            fill: "transparent",
            opacity: 0.2,
            id: this.id,
        });

        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            name: this.name,
            x,
            y,
        });

        group.setAttr("radius", r);

        this.remover.x(-r);
        this.remover.y(-r);

        group.add(this.circle);
        group.add(this.remover);
        return group;
    }
}

export class Rect extends CNTextbox {
    constructor(data) {
        super(data);

        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    setColor(color) {
        this.color = color;
        this.rect.fill(color);
        this.rect.stroke(color);
    }

    resizeMove(x, y, w, h) {
        this.element.x(x);
        this.element.y(y);
        this.rect.width(w);
        this.rect.height(h);
    }

    static toJSON(attrs) {
        const x = attrs.x;
        const y = attrs.y;
        const width = attrs.width;
        const height = attrs.height;
        const startX = x;
        const startY = y;
        const endX = startX + width;
        const endY = startY + height;
        const id = attrs.id;
        const name = attrs.name;
        return {
            t: "addTextBox",
            d: [
                attrs.fill,
                attrs.fill,
                attrs.strokeWidth / 2,
                startX,
                startY,
                id,
                name,
                false,
                endX,
                endY,
            ],
        };
    }

    getVars() {
        const x = this.tool.d[3];
        const y = this.tool.d[4];
        const endX = this.tool.d[8];
        const endY = this.tool.d[9];

        const width = endX - x;
        const height = endY - y;

        return {
            x,
            y,
            width,
            height,
        };
    }

    buildElement() {
        const { x, y, width, height } = this.getVars();

        this.rect = new Konva.Rect({
            stroke: this.color,
            strokeWidth: 3,
            fill: this.color,
            opacity: 0.2,
            width,
            height,
        });

        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            name: this.name,
            x,
            y,
        });

        this.remover.x(-20);
        this.remover.y(-20);

        group.add(this.rect);
        group.add(this.remover);

        return group;
    }
}

export class RectOutline extends Rect {
    constructor(data) {
        super(data);

        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    resizeMove(x, y, w, h) {
        this.element.x(x);
        this.element.y(y);
        this.rect.width(w);
        this.rect.height(h);
    }

    setColor(color) {
        this.color = color;
        this.rect.stroke(color);
    }

    static toJSON(attrs) {
        const x = attrs.x;
        const y = attrs.y;
        const width = attrs.width;
        const height = attrs.height;
        const startX = x;
        const startY = y;
        const endX = startX + width;
        const endY = startY + height;
        const id = attrs.id;
        const name = attrs.name;
        return {
            t: "addTextBox",
            d: [
                attrs.stroke,
                attrs.stroke,
                attrs.strokeWidth / 2,
                startX,
                startY,
                id,
                name,
                false,
                endX,
                endY,
            ],
        };
    }
    buildElement() {
        const { x, y, width, height } = this.getVars();

        this.rect = new Konva.Rect({
            stroke: this.color,
            strokeWidth: STROKE_MAP[this.size],
            fill: "transparent",
            opacity: 0.2,
            width,
            height,
        });

        const group = new Konva.Group({
            draggable: true,
            id: this.id,
            name: this.name,
            x,
            y,
        });

        this.remover.x(-20);
        this.remover.y(-20);

        group.add(this.rect);
        group.add(this.remover);

        return group;
    }
}

export class CircleFixed extends CNTextbox {
    constructor(tool) {
        super(tool);

        this.position = "offense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    setColor(color) {
        this.color = color;
        this.circle.stroke(color);
    }
    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.circle = new Konva.Circle({
            radius,
            stroke: this.color,
            strokeWidth: STROKE_MAP[this.size],
        });
        const group = new Konva.Group({
            id: this.id,
            name: this.name,
            draggable: true,
            x: this.x + radius,
            y: this.y + radius,
        });

        group.add(this.circle);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);

        return group;
    }
}

export class CircleFilled extends CNTextbox {
    constructor(tool) {
        super(tool);

        this.position = "offense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    setColor(color) {
        this.color = color;
        this.circle.fill(color);
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.circle = new Konva.Circle({
            radius: radius,
            fill: this.color,
        });

        const group = new Konva.Group({
            id: this.id,
            draggable: true,
            x: this.x + radius,
            y: this.y + radius,
            name: this.name,
        });
        group.add(this.circle);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);

        return group;
    }
}

export class CircleWithCross extends CNTextbox {
    constructor(tool) {
        super(tool);

        this.position = "offense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }
    setColor(color) {
        this.color = color;
        this.circle.stroke(color);
        this.path.fill(color);
    }
    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.circle = new Konva.Circle({
            radius: radius - STROKE_MAP_RADIUS[this.size] / 2,
            fill: null,
            stroke: this.color,
            strokeWidth: STROKE_MAP[this.size],
        });
        const pathScale = PATH_MAP[this.size];
        this.path = new Konva.Path({
            data: "M28,29a1,1,0,0,1-.71-.29l-24-24A1,1,0,0,1,4.71,3.29l24,24a1,1,0,0,1,0,1.42A1,1,0,0,1,28,29Z, M4,29a1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.42l24-24a1,1,0,1,1,1.42,1.42l-24,24A1,1,0,0,1,4,29Z",
            fill: this.color,
            scale: {
                x: pathScale,
                y: pathScale,
            },
            offset: {
                x: 16,
                y: 16,
            },
        });

        const group = new Konva.Group({
            name: this.name,
            id: this.id,
            x: this.x + 25,
            y: this.y + 25,
            draggable: true,
        });
        group.add(this.circle);
        group.add(this.path);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);
        return group;
    }
}

export class Cross extends CNTextbox {
    constructor(tool) {
        super(tool);

        this.position = "defense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    setColor(color) {
        this.color = color;
        this.path.fill(color);
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.circle = new Konva.Circle({
            radius,
            fill: null,
        });
        const pathScale = PATH_MAP[this.size];
        this.path = new Konva.Path({
            data: "M28,29a1,1,0,0,1-.71-.29l-24-24A1,1,0,0,1,4.71,3.29l24,24a1,1,0,0,1,0,1.42A1,1,0,0,1,28,29Z, M4,29a1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.42l24-24a1,1,0,1,1,1.42,1.42l-24,24A1,1,0,0,1,4,29Z",
            fill: this.color,
            offset: {
                x: 16,
                y: 16,
            },
            scale: {
                x: pathScale,
                y: pathScale,
            },
        });

        const group = new Konva.Group({
            name: this.name,
            id: this.id,
            x: this.x + radius,
            y: this.y + radius,
            draggable: true,
        });
        group.add(this.circle);
        group.add(this.remover);
        group.add(this.path);
        return group;
    }
}

const playerTextBase = {
    align: "center",
    fontSize: 17,
    fontFamily: "Calibri",
    fill: "black",
    fontFamily: "monospace",
};

export class Player extends CNTextbox {
    setColor(color) {
        this.color = color;
        this.playerText.fill(color);
        this.playerContainer?.stroke(color);
    }
    setEditorAction(callback) {
        const _this = this;
        function edit() {
            const textNode = _this.playerText;
            const stage = textNode.getStage();
            const textPosition = textNode.absolutePosition();
            const stageBox = stage.container().getBoundingClientRect();
            textNode.hide();

            const areaPosition = {
                x: stageBox.left + textPosition.x - textNode.width(),
                y: stageBox.top + textPosition.y - textNode.height() / 3,
            };

            const scale = stage.scale();

            const textarea = document.createElement("textarea");
            document.body.appendChild(textarea);
            const fontSize = textNode.fontSize() * scale.x;
            const width = textNode.width() - textNode.padding() * 2;
            const height = textNode.height() - textNode.padding() * 2 + 5;

            textarea.value = textNode.text();
            textarea.style.position = "absolute";
            textarea.style.top = areaPosition.y + "px";
            textarea.style.left = areaPosition.x + "px";
            textarea.style.width = width * 2 + "px";
            textarea.style.height = height + "px";
            textarea.style.fontSize = fontSize + "px";
            textarea.style.border = "none";
            textarea.style.padding = "0px";
            textarea.style.margin = "0px";
            textarea.style.background = "none";
            textarea.style.outline = "none";
            textarea.style.resize = "none";
            textarea.style.lineHeight = textNode.lineHeight();
            textarea.style.fontFamily = textNode.fontFamily();
            textarea.style.textAlign = "center";
            textarea.style.color = textNode.fill();
            textarea.focus();
            textarea.select();

            textarea.addEventListener("keydown", function (e) {
                if (e.key === "Enter") {
                    e.preventDefault();
                    textNode.text(textarea.value);
                    callback(textarea.value);
                    textarea.blur();
                    textNode.show();
                    textNode.offsetX(textNode.width() / 2);
                    textNode.offsetY(textNode.height() / 2 - 1);
                    textarea.remove();
                    setTimeout(function () {
                        window.scrollTo(0, -100);
                    }, 100);
                }

                if (e.key === "Escape") {
                    e.preventDefault();
                    textarea.blur();
                    textNode.show();
                    textarea.remove();

                    setTimeout(function () {
                        window.scrollTo(0, -100);
                    }, 100);
                }
            });

            textarea.addEventListener("blur", () => {
                textNode.text(textarea.value);
                callback(textarea.value);
                textarea.blur();
                textNode.show();
                textNode.offsetX(textNode.width() / 2);
                textNode.offsetY(textNode.height() / 2 - 1);
                textarea.remove();

                setTimeout(function () {
                    window.scrollTo(0, -100);
                }, 100);
            });

            textarea.addEventListener("input", function (event) {
                if (textarea.value.length > 2) {
                    textarea.value = textarea.value.slice(0, 2);
                }
            });
        }
        this.element.on("click", edit);
        this.element.on("dbltap", edit);
    }
}

export class Offense extends Player {
    constructor(data) {
        super(data);

        this.position = "offense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.playerText = new Konva.Text({
            ...playerTextBase,
            fontSize: FONT_SIZE_MAP_OUTLINE[this.size],
            text: this.text(),
            id: this.id + "-text",
            fill: this.color,
        });
        this.playerText.offsetX(this.playerText.width() / 2);
        this.playerText.offsetY(this.playerText.height() / 2 - 1);
        this.playerContainer = new Konva.Circle({
            radius: radius - STROKE_MAP_RADIUS[this.size] / 2,
            fill: null,
            stroke: this.color,
            strokeWidth: STROKE_MAP[this.size],
        });

        const group = new Konva.Group({
            dragable: true,
            x: this.x + radius,
            y: this.y + radius,
            name: this.name,
            id: this.id,
        });

        group.add(this.playerContainer);
        group.add(this.playerText);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);
        group.draggable(true);

        return group;
    }
}

export class Defense extends Player {
    constructor(data) {
        super(data);

        this.position = "defense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.playerText = new Konva.Text({
            ...playerTextBase,
            fontSize: FONT_SIZE_MAP[this.size],
            id: this.id + "-text",
            text: this.text(),
            fill: this.color,
        });
        this.playerText.offsetX(this.playerText.width() / 2);
        this.playerText.offsetY(this.playerText.height() / 2 - 1);
        const playerContainer = new Konva.Circle({
            radius: radius - STROKE_MAP_RADIUS[this.size] / 2,
            strokeWidth: STROKE_MAP[this.size],
        });

        const group = new Konva.Group({
            dragable: true,
            x: this.x + radius,
            y: this.y + radius,
            name: this.name,
            id: this.id,
        });

        group.add(playerContainer);
        group.add(this.playerText);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);
        group.draggable(true);

        return group;
    }
}

export class OffenseBasket extends Player {
    constructor(data) {
        super(data);

        this.position = "offense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.playerText = new Konva.Text({
            ...playerTextBase,
            fontSize: FONT_SIZE_MAP_OUTLINE[this.size],
            id: this.id + "-text",
            text: this.text(),
            fill: this.color,
        });
        const patternSize = PATTERN_MAP[this.size];
        this.playerText.offsetX(this.playerText.width() / 2);
        this.playerText.offsetY(this.playerText.height() / 2 - 1);
        this.playerContainer = new Konva.Circle({
            radius: radius - STROKE_MAP_RADIUS[this.size] / 2,
            strokeWidth: STROKE_MAP[this.size],
            stroke: this.color,
            fillPatternImage: this.basketImage,
            fillPatternScale: {
                x: patternSize / this.basketImage.width,
                y: patternSize / this.basketImage.width,
            },
            fillPatternOffset: {
                x: this.basketImage.width / 2,
                y: this.basketImage.height / 2,
            },
        });

        const group = new Konva.Group({
            x: this.x + radius,
            y: this.y + radius,
            name: this.name,
            draggable: true,
            id: this.id,
        });

        group.add(this.playerContainer);
        group.add(this.playerText);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);
        group.draggable(true);

        return group;
    }
}

export class PlayerX extends Player {
    constructor(data) {
        super(data);

        this.position = "defense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    setColor(color) {
        this.color = color;
        this.playerText.fill(color);
        this.playerBg.fill(color);
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        this.playerText = new Konva.Text({
            ...playerTextBase,
            id: this.id + "-text",
            text: this.text(),
            fontSize: FONT_SIZE_MAP[this.size],
            fill: this.color,
        });
        this.playerText.offsetX(this.playerText.width() / 2);
        this.playerText.offsetY(this.playerText.height() / 2 - 1);

        this.playerContainer = new Konva.Circle({
            radius: radius - STROKE_MAP_RADIUS[this.size] / 2,
        });

        const pathScale = PATH_MAP[this.size];
        this.playerBg = new Konva.Path({
            data: "M28,29a1,1,0,0,1-.71-.29l-24-24A1,1,0,0,1,4.71,3.29l24,24a1,1,0,0,1,0,1.42A1,1,0,0,1,28,29Z, M4,29a1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.42l24-24a1,1,0,1,1,1.42,1.42l-24,24A1,1,0,0,1,4,29Z",
            fill: this.color,
            offset: {
                x: 16,
                y: 16,
            },
            scale: {
                x: pathScale,
                y: pathScale,
            },
            opacity: 0.25,
        });

        const group = new Konva.Group({
            x: this.x + radius,
            y: this.y + radius,
            name: this.name,
            draggable: true,
            id: this.id,
        });

        group.add(this.playerContainer);
        group.add(this.playerBg);
        group.add(this.playerText);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);
        group.draggable(true);

        return group;
    }
}

export class Icon extends CNTextbox {
    constructor(data) {
        super(data);

        this.updatingIcon = false;

        this.withBorder = true;
        this.position = "offense";
        if (this.name.indexOf("no-border") !== -1) {
            this.withBorder = false;
            this.position = "defense";
        }
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        const iconName = this.name.split(":")[1];
        const iconIndex = ICONS.indexOf(iconName);

        this.playerContainer = new Konva.Circle({
            radius: radius - STROKE_MAP_RADIUS[this.size] / 2,
            strokeWidth: STROKE_MAP[this.size],
            stroke: this.withBorder ? this.color : "transparent",
        });

        const imageSize = IMAGE_SIZE_MAP[this.size];
        this.image = new Konva.Image({
            width: imageSize,
            height: imageSize,
            x: imageSize / -2,
            y: imageSize / -2,
        });

        this.updateIcon(iconIndex);

        const group = new Konva.Group({
            dragable: true,
            x: this.x + radius,
            y: this.y + radius,
            name: this.name,
            id: this.id,
        });

        group.add(this.playerContainer);
        group.add(this.image);
        this.hitbox = addPlayerHitbox(group, radius);
        group.add(this.remover);
        group.draggable(true);

        return group;
    }

    setClickAction(callback) {
        const handleClick = (event) => {
            showPlayerHitbox(this.hitbox);
            callback(event);
        };
        this.element.on("click", handleClick);
        this.element.on("tap", handleClick);
    }

    setColor(color) {
        this.color = color;
        if (this.withBorder) {
            this.playerContainer.stroke(color);
        }
    }

    async updateIcon(index) {
        // Track the latest requested icon change
        this.latestIconRequestId = (this.latestIconRequestId || 0) + 1;
        let currentRequestId = this.latestIconRequestId;
        this.iconIndex = index;

        // If another update is already running, exit early
        if (this.updatingIcon) {
            return;
        }

        this.updatingIcon = true;

        while (currentRequestId === this.latestIconRequestId) {
            try {
                const filename = `/default-images/whiteboard/icons/${
                    ICONS[this.iconIndex]
                }.svg`;

                const img = await loadImageAsset(filename, 500, 500);

                // Only apply if this is still the latest request
                if (currentRequestId === this.latestIconRequestId) {
                    this.image.image(img);
                }
            } catch (e) {
                console.error(e);
            }

            // Check if a newer request came in while we were loading
            if (currentRequestId !== this.latestIconRequestId) {
                currentRequestId = this.latestIconRequestId;
                continue; // Loop again to process the new one
            }

            break;
        }

        this.updatingIcon = false;
    }
}

export class ShadedCross extends CNTextbox {
    constructor(tool) {
        super(tool);
        this.shapeIndexes = [0, 0, 4, 0, 0];
        this.circleColors = this.shapeIndexes.map(() => this.color);
        this.selectedCircleIndex = null;

        this.position = "offense";
        this.element = this.buildElement();
        this.element.setAttrs({
            instance: this,
        });
    }
    calcPadding(id) {
        const radius = RADIUS_MAP[this.size];
        return id * ((radius - STROKE_MAP_RADIUS[this.size]) * 2 + radius);
    }

    setColor(color) {}

    get hitbox() {
        return this.hitboxes?.[this.selectedCircleIndex];
    }

    getSelectedColor() {
        return this.circleColors[this.selectedCircleIndex] ?? this.color;
    }

    setShadedColor(index, color) {
        this.circleColors[index] = color;
        this.circles[index].attrs.setColor(color);
    }

    setChangeColorRequestAction(callback) {
        this.changeColorRequestCallback = callback;
    }

    shadeTextbox(posIndex, nextIndex) {
        const c = this.circles[posIndex];
        c.attrs.changeShade(nextIndex);
    }

    /* - outline
    - line
    - right filled
    - left filled
    - cross
    - filled
    - pattern */
    stripes = `
M16,32L18,34L34,18L32,16Z
M9,32L11,34L34,11L32,9Z
M4,30L6,32L32,6L30,4Z
M1,26L3,28L28,3L26,1Z
M-2,22L0,24L24,0L22,-2Z
M-3,16L-1,18L18,-1L16,-3Z
`;

    shapeMaps = [
        "M0,0",
        "M15,0h2v35h-2z",
        "M15,-2A16,16,0,0,1,33,16A16,16,0,0,1,15,32",
        "M16,0A16,16,0,0,0,0,16A16,16,0,0,0,16,32",
        "M28,29a1,1,0,0,1-.71-.29l-24-24A1,1,0,0,1,4.71,3.29l24,24a1,1,0,0,1,0,1.42A1,1,0,0,1,28,29Z, M4,29a1,1,0,0,1-.71-.29,1,1,0,0,1,0-1.42l24-24a1,1,0,1,1,1.42,1.42l-24,24A1,1,0,0,1,4,29Z",
        "M0,0",
        this.stripes,
    ];

    buildCircle(posIndex, shapeIndex) {
        let currentColor = this.color;
        const group = new Konva.Group({
            x: this.calcPadding(posIndex, this.size),
        });

        const radius = RADIUS_MAP[this.size];
        const circle = new Konva.Circle({
            radius: radius - STROKE_MAP[this.size] / 2,
            stroke: currentColor,
            strokeWidth: STROKE_MAP[this.size],
            name: `circle${posIndex}`,
        });

        const pathScale = PATH_MAP[this.size];
        const path = new Konva.Path({
            name: `path${posIndex}`,
            fill: currentColor,
            scale: {
                x: pathScale,
                y: pathScale,
            },
            offset: {
                x: 16,
                y: 16,
            },
        });

        group.add(circle);

        // reset
        circle.fill(null);

        switch (shapeIndex) {
            case 5:
                circle.fill(currentColor);
                break;
            default:
                circle.fill(null);
                path.data(this.shapeMaps[shapeIndex]);
                break;
        }
        group.add(path);
        this.hitboxes[posIndex] = addPlayerHitbox(group, radius);
        const _this = this;
        group.setAttrs({
            setColor: function color(color) {
                currentColor = color;
                circle.stroke(color);
                path.fill(color);
                if (_this.shapeIndexes[posIndex] === 5) {
                    circle.fill(color);
                } else {
                    circle.fill(null);
                }
            },
            changeShade: function changeShape(shapeIndex) {
                _this.shapeIndexes[posIndex] = shapeIndex;
                path.data(_this.shapeMaps[shapeIndex]);
                if (shapeIndex === 5) {
                    circle.fill(currentColor);
                } else {
                    circle.fill(null);
                }
            },
        });

        group.on("click tap", () => {
            const nextIndex =
                (_this.shapeIndexes[posIndex] + 1) % _this.shapeMaps.length;
            _this.changeColorRequestCallback(posIndex, currentColor, nextIndex);
        });

        return group;
    }

    buildElement() {
        const radius = RADIUS_MAP[this.size];
        const group = new Konva.Group({
            id: this.id,
            name: this.name,
            draggable: true,
            x: this.x + radius,
            y: this.y + radius,
        });
        this.circles = [];
        this.hitboxes = [];
        for (let i = 0; i < this.shapeIndexes.length; i++) {
            this.circles[i] = this.buildCircle(i, this.shapeIndexes[i]);
            group.add(this.circles[i]);
        }
        group.add(this.remover);

        return group;
    }
}
